// ============================================================================
// SMART ACCOUNT VISION — PRICING ENGINE
// Pure functions. Reads everything from data/pricing.js config.
// ============================================================================
import { SERVICE_MAP, taxConfig } from "@/data/pricing";

const ceilDiv = (a, b) => Math.ceil(a / b);

// Evaluate a tiered price with optional overage formula
export function tierEval(tiers, overage, n) {
  const x = Math.max(0, Number(n) || 0);
  for (const [max, price] of tiers) {
    if (x <= max) return price;
  }
  if (overage && x > overage.threshold) {
    return overage.base + ceilDiv(x - overage.threshold, overage.per) * overage.step;
  }
  return tiers[tiers.length - 1][1];
}

// Normal monthly document processing price for a given document count
function docMonthly(n) {
  const d = SERVICE_MAP.docProcessing;
  return tierEval(d.tiers, d.overage, n);
}

// Custom per-service formulas
const CUSTOM = {
  bankRec: (wl) => {
    const txnTiers = [[100, 155], [250, 235], [500, 390], [1000, 630], [1500, 785], [2500, 1180]];
    const base = tierEval(txnTiers, { threshold: 2500, base: 1180, step: 235, per: 500 }, wl.bankTransactions);
    const extra = Math.max(0, (Number(wl.bankAccounts) || 1) - 1) * 75;
    return base + extra;
  },
  creditCard: (wl) => {
    const t = [[100, 75], [250, 155], [500, 235]];
    const perCard = tierEval(t, { threshold: 500, base: 235, step: 75, per: 250 }, wl.cardTransactions);
    return perCard * Math.max(1, Number(wl.cards) || 1);
  },
  vatVoluntaryDisclosure: (wl) => 785 + Math.max(0, (Number(wl.periods) || 1) - 1) * 235,
  ftaAuditAssist: (wl) => 470 + Math.max(0, (Number(wl.hours) || 1) - 2) * 235,
  externalAudit: (wl) => {
    const r = Number(wl.annualRevenue) || 0;
    let base;
    if (r <= 1000000) base = 2360;
    else if (r <= 3000000) base = 3150;
    else if (r <= 10000000) base = 4725;
    else if (r <= 25000000) base = 6300;
    else base = 7875 + ceilDiv(r - 25000000, 10000000) * 785;
    return base + (Number(wl.auditBranches) || 0) * 390;
  },
  backlogStandard: (wl) => docMonthly(wl.backlogDocs) * 1.25 * Math.max(1, Number(wl.backlogMonths) || 1),
  backlogCleanup: (wl) => docMonthly(wl.backlogDocs) * 1.5 * Math.max(1, Number(wl.backlogMonths) || 1),
};

// Resolve workload with defaults + min clamping
function resolveWorkload(service, wl = {}) {
  const out = { ...wl };
  (service.inputs || []).forEach((inp) => {
    let v = out[inp.key];
    if (v === undefined || v === null || v === "" || isNaN(Number(v))) v = inp.def;
    v = Math.max(inp.min, Number(v));
    out[inp.key] = v;
  });
  return out;
}

// Compute the price of a single service given its workload
export function computeServicePrice(service, wl = {}) {
  const w = resolveWorkload(service, wl);
  if (service.model === "custom") return CUSTOM[service.id] ? CUSTOM[service.id](w) : 0;
  if (service.model === "tiered") return tierEval(service.tiers, service.overage, w[service.inputKey]);
  // flat (optionally multiplied by a count input)
  let price = service.price || 0;
  if (service.countKey) price *= Math.max(1, Number(w[service.countKey]) || 1);
  return price;
}

const bucketOf = (billing) => (billing === "month" ? "monthly" : billing === "year" ? "annual" : "onetime");

// Compute grouped totals from a selection map: { serviceId: workloadObj }
export function computeTotals(selection, opts = {}) {
  const ids = Object.keys(selection);
  const includedFree = new Set();
  ids.forEach((id) => {
    const s = SERVICE_MAP[id];
    if (s && s.includes) s.includes.forEach((x) => includedFree.add(x));
  });

  const items = { monthly: [], onetime: [], annual: [] };

  ids.forEach((id) => {
    const s = SERVICE_MAP[id];
    if (!s) return;
    let price;
    let included = false;
    if (s.alwaysIncluded) { price = 0; included = true; }
    else if (includedFree.has(id)) { price = 0; included = true; }
    else if (s.freeWith12mo && opts.annualContract) { price = 0; included = true; }
    else price = computeServicePrice(s, selection[id]);

    items[bucketOf(s.billing)].push({ id, name: s.name, billing: s.billing, price, included });
  });

  const sum = (arr) => arr.reduce((a, i) => a + i.price, 0);
  return {
    items,
    includedFree,
    monthlyTotal: sum(items.monthly),
    onetimeTotal: sum(items.onetime),
    annualTotal: sum(items.annual),
  };
}

// Starting-from monthly price for a preset (lowest workload tiers)
export function presetStartingMonthly(preset) {
  const selection = {};
  preset.services.forEach((id) => { selection[id] = {}; });
  return computeTotals(selection).monthlyTotal;
}

// Build an initial selection map (default workloads) from a list of service ids
export function selectionFromIds(ids) {
  const sel = {};
  ids.forEach((id) => { sel[id] = {}; });
  return sel;
}

// Format a number as AED, trimming .00 for whole numbers
export function fmt(n) {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  const str = Number.isInteger(v) ? v.toLocaleString("en-US") : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `AED ${str}`;
}

export { taxConfig };
